import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import Table from "../../core/pagination/datatable";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { renderTooltip } from "../../redux/helper";
import { setheadertoggle } from "../../redux/toggle";
import {
  fetchCategory,
  fetchGroup,
  fetchProduct,
  fetchSection,
} from "../../redux/userSetting";
import { getCategoryMasterList } from "../../services/MasterApiServices";
import {
  exportMasterExcel,
  exportMasterPDF,
  printMasterReport,
} from "../../core/utils/MasterReportUtils";
import {
  ACTIVE_FILTER_OPTIONS,
  filterByActiveStatus,
} from "../../core/utils/ProductActiveFilter";

function ProductPriceReports() {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { togglehead } = useSelector((state) => state.headerToggle);
  const { productList, sectionList, categoryList, groupList } = useSelector(
    (state) => state.usersetting
  );

  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [activeFilter, setActiveFilter] = useState("hideInactive");
  const [categoryGroupOptions, setCategoryGroupOptions] = useState([]);

  const loadCategoryGroups = async () => {
    try {
      const response = await getCategoryMasterList();
      setCategoryGroupOptions(response || []);
    } catch (error) {
      setCategoryGroupOptions([]);
    }
  };

  const handlerefresh = () => {
    dispatch(fetchProduct(branch));
    dispatch(fetchSection(branch.guid));
    dispatch(fetchCategory(branch.guid));
    dispatch(fetchGroup(branch.guid));
    setSearchText("");
    setSelectedCategory("");
    setSelectedCategoryGroup("");
    setSelectedGroup("");
    setActiveFilter("hideInactive");
  };

  useEffect(() => {
    handlerefresh();
    loadCategoryGroups();
  }, []);

  const categoryGuidToGroupGuid = useMemo(() => {
    const map = {};
    categoryList?.forEach((c) => {
      map[c.guid] = c.categoryMasterGuID || "";
    });
    return map;
  }, [categoryList]);

  const productData = useMemo(() => {
    return (productList || []).map((p) => {
      const dynamicFields = {};
      p.sectionWiseProductDetailList?.forEach((sec, index) => {
        dynamicFields[`section_${index}`] = sec.price ?? "";
      });
      return {
        ...p,
        categoryName: p.category?.name || "",
        groupGuid: p.group?.guid || "",
        categoryGroupGuid: categoryGuidToGroupGuid[p.category?.guid] || "",
        ...dynamicFields,
      };
    });
  }, [productList, categoryGuidToGroupGuid]);

  const filteredProducts = filterByActiveStatus(
    productData.filter((item) => {
      const search = searchText.toLowerCase();
      const matchesSearch =
        !search ||
        item.name?.toLowerCase().includes(search) ||
        item.code?.toLowerCase().includes(search) ||
        item.barcode?.toLowerCase().includes(search);

      const matchesCategory =
        !selectedCategory || item.category?.guid === selectedCategory;

      const matchesCategoryGroup =
        !selectedCategoryGroup || item.categoryGroupGuid === selectedCategoryGroup;

      const matchesGroup = !selectedGroup || item.groupGuid === selectedGroup;

      return matchesSearch && matchesCategory && matchesCategoryGroup && matchesGroup;
    }),
    activeFilter
  );

  const handletogglehead = () => {
    dispatch(setheadertoggle(!togglehead));
  };

  const staticColumns = [
    {
      title: "Name",
      dataIndex: "name",
      ellipsis: true,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Code",
      dataIndex: "code",
      width: 80,
      sorter: (a, b) => (a.code || "").localeCompare(b.code || ""),
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      width: 90,
      sorter: (a, b) => (a.barcode || "").localeCompare(b.barcode || ""),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      ellipsis: true,
      sorter: (a, b) => (a.categoryName || "").localeCompare(b.categoryName || ""),
    },
  ];

  const dynamicColumns = sectionList?.map((obj, index) => ({
    title: obj?.name || `Section ${index + 1}`,
    dataIndex: `section_${index}`,
    width: 90,
    render: (value) =>
      value !== "" && value !== undefined && value !== null
        ? Number(value).toFixed(2)
        : "",
  }));

  const columns = [...staticColumns, ...(dynamicColumns || [])];

  const getActiveTabData = () => {
    const fieldMapping = {};
    columns.forEach((col) => {
      fieldMapping[col.title] = col.dataIndex;
    });
    return { data: filteredProducts, columns, fieldMapping, Rname: "Product Price Report" };
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Product Price Report</h4>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("PDF")}>
                  <Link onClick={() => exportMasterPDF(getActiveTabData)}>
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Excel")}>
                  <Link onClick={() => exportMasterExcel(getActiveTabData)}>
                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Print")}>
                  <Link onClick={() => printMasterReport(getActiveTabData)}>
                    <i data-feather="printer" className="feather-printer" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    onClick={handlerefresh}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={togglehead ? "active" : ""}
                    onClick={handletogglehead}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top" style={{ flexWrap: "wrap", rowGap: "12px" }}>
                <div className="search-set">
                  <div className="search-input">
                    <Link to="#" className="btn btn-searchset">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-search"
                      >
                        <circle cx={11} cy={11} r={8} />
                        <line x1={21} y1={21} x2="16.65" y2="16.65" />
                      </svg>
                    </Link>
                    <div id="DataTables_Table_0_filter" className="dataTables_filter">
                      <label>
                        <input
                          type="search"
                          className="form-control form-control-sm"
                          placeholder="Search by Name/Code/Barcode"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-12 md-6 mb-2 d-flex flex-wrap align-items-center gap-3">
                  <select
                    className="form-select"
                    style={{ width: "180px" }}
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categoryList?.map((obj) => (
                      <option key={obj.guid} value={obj.guid}>
                        {obj.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-select"
                    style={{ width: "180px" }}
                    value={selectedCategoryGroup}
                    onChange={(e) => setSelectedCategoryGroup(e.target.value)}
                  >
                    <option value="">All Category Groups</option>
                    {categoryGroupOptions?.map((obj) => (
                      <option key={obj.guid} value={obj.guid}>
                        {obj.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-select"
                    style={{ width: "180px" }}
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    <option value="">All Groups</option>
                    {groupList?.map((obj) => (
                      <option key={obj.guid} value={obj.guid}>
                        {obj.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-select"
                    style={{ width: "180px" }}
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                  >
                    {ACTIVE_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="table-responsive product-list">
                <Table columns={columns} dataSource={filteredProducts} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPriceReports;
